import { Button, Dialog, DialogContent, DialogTitle, Grid, Stack, TextField, } from "@mui/material";
import PaperDraggable from "../../PaperDraggable";
import { formatarInputValorMonetário, formatarMonetárioParaNúmero, formatarValorMonetario } from "../../../helpers/masks";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMemo } from "react";
import { DadoValorPrinter } from "../DisplayDados/DadoValorPrinter";

export default function DialogDinheiro({
    open,
    valor,
    defaultValues,
    isEdit,
    onSubmit,
    onClose
}: DialogDinheiroProps) {

    const methods = useForm<TPagamentoDinheiro>({
        defaultValues: {
            valor: defaultValues?.valor ? formatarValorMonetario(defaultValues?.valor) : formatarValorMonetario(valor),
            valor_recebido: defaultValues?.valor_recebido ? formatarValorMonetario(defaultValues?.valor_recebido) : "",
            troco: defaultValues?.troco ? formatarValorMonetario(defaultValues?.troco) : formatarValorMonetario("0")
        }
    })

    const valor_recebido = methods.watch("valor_recebido");

    const { handleSubmit, control } = methods;

    const handleClickSubmit = (data: TPagamentoDinheiro) => {
        const { valor, valor_recebido, troco } = methods.getValues();
        const params = {
            valor: formatarMonetárioParaNúmero(valor),
            valor_recebido: formatarMonetárioParaNúmero(valor_recebido),
            troco: formatarMonetárioParaNúmero(troco)
        }
        onSubmit(params);
    }

    const podeContinuar = useMemo(() => {
        return !(formatarMonetárioParaNúmero(valor_recebido) >= valor)
    }, [valor_recebido, valor])

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={onClose}
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
                fullWidth
                maxWidth={"xs"}
            >
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Dinheiro</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name="valor"
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        required
                                        disabled
                                        label="Valor"
                                        fullWidth
                                        error={invalid}
                                        helperText={error ? "Campo obrigatório" : ""}
                                        value={field.value}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name="valor_recebido"
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { invalid, error } }) => (
                                    <TextField
                                        required
                                        label="Valor recebido"
                                        fullWidth
                                        error={invalid}
                                        helperText={error ? "Campo obrigatório" : ""}
                                        value={field.value}
                                        onChange={(e) => {
                                            const formattedValue = formatarInputValorMonetário(e.target.value);
                                            const { valor } = methods.getValues();
                                            const troco = formatarMonetárioParaNúmero(formattedValue) - formatarMonetárioParaNúmero(valor);
                                            if (troco >= 0) {
                                                methods.setValue('troco', formatarValorMonetario(troco));
                                            } else {
                                                methods.setValue('troco', formatarValorMonetario("0"));
                                            }
                                            field.onChange(formattedValue);
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Stack direction={'row'} justifyContent={'space-between'}>
                                <Controller
                                    name="troco"
                                    control={control}
                                    render={({ field }) => (
                                        <DadoValorPrinter
                                            titulo="Troco"
                                            valor={field.value}
                                        />
                                    )}
                                />
                                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                                    <Button onClick={onClose} color="primary">
                                        Cancelar
                                    </Button>
                                    <Button disabled={podeContinuar} onClick={handleSubmit(handleClickSubmit)} color="primary" variant="contained" >
                                        {isEdit ? "Editar" : "Receber"}
                                    </Button>
                                </Stack>
                            </Stack>

                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>
        </FormProvider>
    )
}

interface DialogDinheiroProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: TPagamentoDinheiroParams) => void;
    valor: number;
    defaultValues?: TPagamentoDinheiroParams;
    isEdit: boolean;
}

export type TPagamentoDinheiro = {
    valor: string;
    valor_recebido: string;
    troco: string;
}

export type TPagamentoDinheiroParams = {
    valor: number;
    valor_recebido: number;
    troco: number;
}